"use client";

import React, { useState } from "react";
import Image from "next/image";
import styles from "./premium-hero.module.css";
import { ListingData } from "@/types/types";
import { getMedia } from "@/lib/media/shared";
import PhotoPreviewDialog from "../standard/photo-preview-dialog"; // Adjust path if needed

// Swiper Imports
import { Swiper, SwiperSlide, useSwiper } from "swiper/react";
import { Autoplay, EffectFade, Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/effect-fade";

import {
  IoChevronBack,
  IoChevronForward,
  IoShareSocialOutline,
  IoHeartOutline,
  IoLocationOutline,
  IoPlayCircleOutline,
  IoImagesOutline,
  IoCubeOutline,
  IoMapOutline,
  IoBedOutline,
  IoWaterOutline,
  IoCarOutline,
  IoCalendarOutline,
} from "react-icons/io5";
import { MdOutlineSquareFoot } from "react-icons/md";

interface PremiumHeroProps {
  listingData: ListingData;
  currency?: string;
  locale?: string;
}

export default function PremiumHero({
  listingData,
  currency = "EUR",
  locale = "ro-RO",
}: PremiumHeroProps) {
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);

  // Extract and resolve all photos
  const rawPhotos = Object.values(listingData.photos || {});
  const displayPhotos = rawPhotos.slice(0, 10).map((media) => ({
    mediaId: media.MediaId,
    resolved: getMedia(media, "lg"),
  }));

  // Formatters
  const formattedPrice = new Intl.NumberFormat(locale, {
    style: "currency",
    currency: currency,
    maximumFractionDigits: 0,
  }).format(listingData.price);

  const pricePerSqm =
    listingData.interior_space > 0
      ? new Intl.NumberFormat(locale, {
          style: "currency",
          currency: currency,
          maximumFractionDigits: 0,
        }).format(listingData.price / listingData.interior_space)
      : null;

  const handleOpenGallery = () => setIsGalleryOpen(true);

  return (
    <>
      <section className={styles.premiumHeroSection}>
        {/* =========================================
            1. CINEMATIC VISUAL LAYER (85vh)
            ========================================= */}
        <div className={styles.visualsContainer}>
          {/* --- SWIPER BACKGROUND --- */}
          <Swiper
            modules={[Autoplay, EffectFade]}
            effect="fade"
            autoplay={{ delay: 5000, disableOnInteraction: false }}
            speed={1500}
            allowTouchMove={true} // Allow manual swiping
            onSlideChange={(swiper) => setActiveSlideIndex(swiper.realIndex)}
            className={styles.swiperContainer}
            loop
          >
            <CustomHeroNavigation />
            {displayPhotos.map((photo, index) => (
              <SwiperSlide key={photo.mediaId || index}>
                <div
                  className={styles.slideWrapper}
                  onClick={handleOpenGallery}
                  role="button"
                  aria-label="Deschide galeria foto"
                >
                  {photo.resolved?.src && (
                    <picture style={{ display: "contents" }}>
                      {photo.resolved.webp && (
                        <source
                          srcSet={photo.resolved.webp}
                          type="image/webp"
                        />
                      )}
                      <img
                        src={photo.resolved.src}
                        alt={`${listingData.title} - Foto ${index + 1}`}
                        className={styles.heroImage}
                        // Eagerly load the first image for performance, lazy load the rest
                        loading={index === 0 ? "eager" : "lazy"}
                        fetchPriority={index === 0 ? "high" : "auto"}
                        decoding="async"
                      />
                    </picture>
                  )}
                </div>
              </SwiperSlide>
            ))}
          </Swiper>

          {/* --- SCRIM OVERLAYS FOR TEXT LEGIBILITY --- */}
          <div className={styles.topScrim} />
          <div className={styles.bottomScrim} />

          {/* --- CONTENT LAYER --- */}
          <div className={styles.contentLayer}>
            {/* Top Navigation */}
            <div className={styles.topNav}>
              <div className={styles.breadcrumbs}>
                Acasă &gt; Cumpără &gt; București &gt;{" "}
                <span className={styles.crumbActive}>
                  {listingData.title.slice(0, 30)}...
                </span>
              </div>
              <div className={styles.navActions}>
                <button className={styles.textBtn}>
                  <IoChevronBack /> Înapoi la rezultate
                </button>
                <div className={styles.divider} />
                <button className={styles.textBtn}>
                  Următorul <IoChevronForward />
                </button>
                <div className={styles.iconGroup}>
                  <button className={styles.iconBtn}>
                    <IoShareSocialOutline />
                  </button>
                  <button className={styles.iconBtn}>
                    <IoHeartOutline />
                  </button>
                </div>
              </div>
            </div>

            {/* Main Hero Information */}
            <div className={styles.mainContent}>
              <div className={styles.infoColumn}>
                {/* Badges */}
                <div className={styles.badges}>
                  <span className={styles.badgePremium}>PREMIUM</span>
                  {listingData.new_development && (
                    <span className={styles.badgeNew}>PROIECT NOU</span>
                  )}
                </div>

                {/* Title & Location */}
                <h1 className={styles.title}>{listingData.title}</h1>
                <div className={styles.locationRow}>
                  <IoLocationOutline className={styles.pinIcon} />
                  <span>
                    {listingData.address}, {listingData.city}
                  </span>
                </div>

                {/* Price OR Price On Request */}
                <div className={styles.priceBlock}>
                  {listingData.price_on_request ? (
                    <button className={styles.priceOnRequestBtn}>
                      Preț la cerere
                    </button>
                  ) : (
                    <>
                      <h2 className={styles.price}>{formattedPrice}</h2>
                      {pricePerSqm && (
                        <span className={styles.pricePerSqm}>
                          {pricePerSqm} / mp
                        </span>
                      )}
                    </>
                  )}
                </div>

                {/* CTAs */}
                <div className={styles.ctaGroup}>
                  <button className={styles.primaryCta}>
                    Contactează agentul
                  </button>
                  {listingData.has_presentation_video && (
                    <button className={styles.secondaryCta}>
                      <IoPlayCircleOutline className={styles.ctaIcon} /> Tur
                      video
                    </button>
                  )}
                </div>
              </div>

              {/* Floating Media Actions */}
              <div className={styles.mediaButtons}>
                <button className={styles.mediaBtn} onClick={handleOpenGallery}>
                  <IoImagesOutline /> {rawPhotos.length} Fotografii
                </button>
                {listingData.has_web_tour && (
                  <button className={styles.mediaBtn}>
                    <IoCubeOutline /> Tur 3D
                  </button>
                )}
                <button className={styles.mediaBtn}>
                  <IoMapOutline /> Hartă
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* =========================================
            2. ELEVATED STATS RIBBON (Below Image)
            ========================================= */}
        <div className={styles.statsRibbonContainer}>
          <div className={styles.statsRibbon}>
            {listingData.number_of_bedrooms > 0 && (
              <div className={styles.statItem}>
                <IoBedOutline className={styles.statIcon} />
                <div className={styles.statText}>
                  <span className={styles.statValue}>
                    {listingData.number_of_bedrooms}
                  </span>
                  <span className={styles.statLabel}>Dormitoare</span>
                </div>
              </div>
            )}

            {listingData.number_of_bathrooms > 0 && (
              <div className={styles.statItem}>
                <IoWaterOutline className={styles.statIcon} />
                <div className={styles.statText}>
                  <span className={styles.statValue}>
                    {listingData.number_of_bathrooms}
                  </span>
                  <span className={styles.statLabel}>Băi</span>
                </div>
              </div>
            )}

            <div className={styles.statItem}>
              <IoCarOutline className={styles.statIcon} />
              <div className={styles.statText}>
                <span className={styles.statValue}>{"Da"}</span>
                <span className={styles.statLabel}>Parcare</span>
              </div>
            </div>

            {listingData.interior_space > 0 && (
              <div className={styles.statItem}>
                <MdOutlineSquareFoot className={styles.statIcon} />
                <div className={styles.statText}>
                  <span className={styles.statValue}>
                    {listingData.interior_space} mp
                  </span>
                  <span className={styles.statLabel}>Suprafață utilă</span>
                </div>
              </div>
            )}

            {listingData.lot_size > 0 && (
              <div className={styles.statItem}>
                <MdOutlineSquareFoot className={styles.statIcon} />
                <div className={styles.statText}>
                  <span className={styles.statValue}>
                    {listingData.lot_size} mp
                  </span>
                  <span className={styles.statLabel}>Suprafață teren</span>
                </div>
              </div>
            )}

            {listingData.year_built > 0 && (
              <div className={styles.statItem}>
                <IoCalendarOutline className={styles.statIcon} />
                <div className={styles.statText}>
                  <span className={styles.statValue}>
                    {listingData.year_built}
                  </span>
                  <span className={styles.statLabel}>An construcție</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      <PhotoPreviewDialog
        isOpen={isGalleryOpen}
        onClose={() => setIsGalleryOpen(false)}
        photos={listingData.photos}
        video={listingData.presentation_video_data}
        initialIndex={activeSlideIndex}
      />
    </>
  );
}

function CustomHeroNavigation() {
  const swiper = useSwiper();
  return (
    <>
      <button
        className={`${styles.customNavBtn} ${styles.navLeft}`}
        onClick={(e) => {
          e.stopPropagation(); // Prevents opening the gallery dialog
          swiper.slidePrev();
        }}
        aria-label="Imaginea anterioară"
      >
        <IoChevronBack />
      </button>
      <button
        className={`${styles.customNavBtn} ${styles.navRight}`}
        onClick={(e) => {
          e.stopPropagation(); // Prevents opening the gallery dialog
          swiper.slideNext();
        }}
        aria-label="Imaginea următoare"
      >
        <IoChevronForward />
      </button>
    </>
  );
}
